
import { ActivatedRouteSnapshot, Params, UrlSegment } from "@angular/router";
import { DateTime } from "luxon";

/**
 * Interface of a client side application exception
 *
 * @export
 * @interface ICoreApplicationError
 */
export interface ICoreApplicationError {
  /**
   * Error message
   *
   * @type {string}
   * @memberof ICoreApplicationError
   */
  message: string;
  /**
   * Error stacktrace
   *
   * @type {string}
   * @memberof ICoreApplicationError
   */
  stack: string;
  /**
   * Path where error occurred
   *
   * @type {string}
   * @memberof ICoreApplicationError
   */
  path: string;
  /**
   * User
   *
   * @type {string}
   * @memberof ICoreApplicationError
   */
  username: string;
  /**
   * Error additional information as JSON string
   *
   * @type {string}
   * @memberof ICoreApplicationError
   */
  info: string;
}
/**
 * Core routes enum
 *
 * @export
 * @enum {string}
 */
export enum CoreRoute {
  ResetCredentials = 'reset',
  Login = 'login',
  Home = 'home',
  // ExternalApp = '/eidos/app',
  Readme = 'readme',
  Reset = 'reset',
  Token = 'token',
  NotFound = 'error/404',
  RegisterAccount = "/account/register"
}
/**
 * Eidos routes enum
 *
 * @export
 * @enum {string}
 */
export enum EidosRoute {
  Object = 'object'
}
export enum CoreModuleName {
  Reservation = 'reservation',
  ReservationSetup = 'reservation-setup',
  Eidos = 'eidos',
  CruOp = 'cru-op',
  ResChart = 'res-ch',
  ResApc = 'res-apc',
}

export enum ReservationAdminRoute {
  Exchange = 'exchange-rate',
  SalesAssistant = 'sales-assistant',
  Hotels = 'hotels',
  GeoCity = 'geo',
  SupplierRequestSetup = 'supplier-request-setup',
  SupplierRequestSetupItem = 'supplier-request-setup-item',
  PropertySetup = 'property-setup',
  PropertySetupItem = 'property-setup-detail',
  PackageReports = 'package-reports',
  TopicSetup = 'topic',
  PropertyAmenities = 'amenities',
  Experience = 'experience',
  ExperienceItem = 'experience-detail',
  Travel = 'travel',
  SupplierUnique = 'supplier-unique',
  WaitlistGuaranteeManagement = 'wl-gty-management',
  SanctuaryCampAllocationManagement = 'sanctuary-camp-management'
}
export enum ReservationChart {
  Home = 'home',
}
export enum ReservationShorex {
  Home = 'home',
  ItinerarySetup = 'itinerary-setup',
  ItinerarySetupDetail = 'itinerary-setup-detail',
  ShorexSetup = 'shorex-setup',
  ShorexSetupDetail = 'shorex-setup-detail',
  TurnaroundSetup = 'turnaround-setup',
  ChecklistSetup = 'checklist-setup',
  CruisingOperationSetup = 'cruising-operating',
  Documentation = 'documentation',
  BudgetControl = 'budget-control',
  CruiselineSetup = 'cruiseline-setup',
  PublicHolidaySetup = 'public-holiday-setup',
  WorkedHours = 'worked-hours',
}
export enum ReservationApc {
  Home = 'home',
  Purchase = 'purchase',
  NewPurchase = 'new-purchase',
  Invoice = 'invoice',
  NewInvoice = 'new-invoice',
  CreditNote = 'credit-note',
  Iata = 'iata',
}

/**
 * Reservation routes enum
 *
 * @export
 * @enum {string}
 */
export enum ReservationRoute {
  Home = 'home',
  // EntrySearch = 'entry-search',
  Search = 'search',
  SearchProduct = 'product-search',
  SearchProductNew = 'p-search',
  ShorexPrice = 'shorex-price',
  ProductDetail = 'product-detail',
  ProductAvailability = 'product-avl',
  ExperienceAvailability = 'expirience-avl',
  ReportViewer = 'report-viewer',
  EntityCompare = 'entity-compare',
  Booking = 'booking',
  Availability = 'product-availability',
  SuiteSelection = 'suite-selection',
  CustomPackage = 'custom-package',
  Deposit = 'deposit',
  GoodwillDeposit = 'goodwill-deposit',
  Payment = 'payment',
  Checkout = 'checkout',
  CashReceipts = 'cash-receipts',
  ServiceSetup = 'service-setup',
  PriceSetup = 'price-setup',
  PackageSetup = 'package-setup',
  PackagePriceSetup = 'package-price-setup',
  PromoSetup = 'promo-setup',
  Manifest = 'manifest',
  MyActivity = 'my-activity',
  CommissionPayout = 'commission-payout',
  CommissionPayoutAk = 'commission-payout-ak',
  BoookingItems = 'bkg-items',
  CabinOccupancy = 'cabin-occupancy',
  DocumentList = 'document-list',
  EntrySearch = 'entry-search',
  PowerBI = 'power-bi',
  //move to adm
  InventorySetup = 'inventory-setup',
  InteractiveDeckplan = 'interactive-deckplan',
  ManifestLight = 'product-replacement',
  HeritageDeposit = 'heritage-deposit',
  GeoPath = 'geopath',
  PropertySetup = 'property-setup',
}

/**
 * Reservation main root
 */
export const RESERVATION_ROOT: string = CoreModuleName.Reservation;
export const RES_SETUP_ROOT: string = CoreModuleName.ReservationSetup;
export const RES_SHOREX_ROOT: string = CoreModuleName.CruOp;
export const RES_CHARTS: string = CoreModuleName.ResChart;
export const RES_APC_ROOT: string = CoreModuleName.ResApc;

export type EidosBootstrapColWidth = '-' | '-xs-' | '-sm-' | '-md-' | '-lg-' | '-xl-';
export type EidosBootstrapColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type EidosBootstrapAlias = `col${EidosBootstrapColWidth}${EidosBootstrapColumns}`;
export const EidosSvgNameSpace = "http://www.w3.org/2000/svg";

/**
 * Eidos menu possible sort status
 *
 * @export
 * @enum {number}
 */
export enum EidosMenuOrderStatus {
  Unset = 0,
  AZ = 1,
  ZA = 2
}

/**
 * Keys enum of info saved in local storage
 *
 * @export
 * @enum {string}
 */
export enum LocalStorageKeys {
  USER = 'user',
  RAW_USER = 'raw_user',
  TOKEN = 'mybiz_token',
  REQUESTED_ROUTE = 'requested_route',
  PINNED_MENU = 'pinned_menu',
  DEBUG_MODE = 'debug_mode',
  TRAVELSTUDIO_TOKEN = 'TravelStudio_Token',
  OBJECT_DATA = 'gobj_{id}',
}

/**
 * Keys enum of info saved in session storage
 *
 * @export
 * @enum {number}
 */
export enum SessionStorageKeys {
  OBJECT_DEFINITION = 'object_definition_{id}',
  OBJECT_DATA = 'object_data_{id}_page{page}',
  ENVIRONMENT_PARAMETER = 'env_params_{name}'
}

/**
 * Eidos security check request type
 *
 * @export
 * @enum {number}
 */
export enum SecurityCheckRequestType {
  User,
  Authentication,
}

/**
 * Eidos icon types enum
 *
 * @export
 * @enum {string}
 */
export enum EidosIconType {
  MyBiz = 'image',
  Material = 'material',
  Awesome = 'awesome',
  SVG = 'svg',
  DX = 'dx'
}

/**
 * Type of event messages from child iframe enum
 *
 * @export
 * @enum {string}
 */
export enum EidosIframeBehaviourType {
  Dataloaded = 'dataloaded',
  RefreshToken = 'refresh_token',
  LoginTokenError = 'login_token_error',
  LoginTokenSuccessful = 'login_token_successful',
  Route = 'route',
  Pong = 'pong'
}

/**
 * Type of post messages from child iframe enum
 *
 * @export
 * @enum {number}
 */
export enum EidosIframeEventType {
  Command = "command",
  Version = "version",
  Event = "event"
}



/**
 * Types of profile menu action enumerations
 *
 * @export
 * @enum {string}
 */
export enum MenuActionType {
  Impersonate = 'impersonate',
  StopImpersonate = 'stopImpersonate',
  Settings = 'settings',
  ChangePassword = 'changePassword',
  Logout = 'logout',
  Debug = 'debugMode',
  Refresh = 'refresh',
  Readme = 'readme'
}

/**
 * Types of Eidos debug log severity enum
 *
 * @export
 * @enum {number}
 */
export enum EidosLogSeverity {
  Log,
  Warning,
  Error
}

/**
 * Eidos external app action types
 *
 * @export
 * @enum {number}
 */
export enum EidosExternalAppAction {
  Eidos = 'eobject',
  MyBiz = 'object',
  EmbeddedUrl = 'eurl'
}

/**
 * if input value is a valid ISO datetime, casts input to format string provided
 * If no format is provided, casts it to dd/mm/yyyy if date is at midnight, dd/mm/yyyy hh:mm otherwise
 *
 * @export
 * @param {*} value
 * @param {string} [format]
 * @return {*}  {string}
 */
export function eventuallyDisplayDateAndTime(value: any, format?: string): string {
  value = value.toString();

  const castedTime = castToDateTime(value);
  if (castedTime) {
    const defaultFormat = castedTime.toMillis() === castedTime.startOf('day').toMillis() ? 'dd/LL/yyyy' : 'dd/LL/yyyy HH:mm';
    return castedTime.toFormat(format ?? defaultFormat);
  }

  return value;
}


export function castToDateTime(value: any): DateTime | undefined {
  value = value.toString();

  const timeFromIso = DateTime.fromISO(value);
  if (timeFromIso.isValid) {
    return timeFromIso;
  } else {
    const timefromRFC2822 = DateTime.fromRFC2822(value);
    return timefromRFC2822.isValid ? timefromRFC2822 : undefined;
  }
}

export enum EidosAuthType {
  Form = 'F',
  Windows = 'W',
  Microsoft = 'M',
  Google = 'G'
}
/**
 * Returns URLSegment of Angular ActivatedRouteSnapshot
 *
 * @export
 * @param {ActivatedRouteSnapshot} r
 * @return {*}  {UrlSegment[]}
 */
export function getURLSegmentsFromActivatedRoute(r: ActivatedRouteSnapshot): UrlSegment[] {
  if (!r.parent) {
    return [];
  } else {
    return [...getURLSegmentsFromActivatedRoute(r.parent), ...r.url]
  }
};
/**
 * Returns value of a case insensitive key in params
 *
 * @export
 * @param {{[key: string]: any}} params
 * @param {string} key
 * @return {*}  {(string | undefined)}
 */
export function getCaseInsensitiveParam(params: Params, key: string): string | undefined {
  let value: any;
  for (const paramKey in params) {
    if (paramKey.toString().toLowerCase() === key.toString().toLowerCase()) {
      value = params[paramKey];
      break;
    }
  }
  return value;
}

export interface ICoreNavigationRoute {
  url: Array<string | number>;
  description?: string;
  queryParams?: Params;
  outReservationBranch?: boolean
  /**
   * if true, will navigate even if the url is the same
   *
   * @type {boolean}
   * @memberof ICoreNavigationRoute
   */
  forceNavigate?: boolean;
}
